import React, { useEffect, useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Play, Pause, ArrowCounterClockwise, Timer as TimerIcon, SpeakerHigh, SpeakerSlash, Plus, Minus, StopCircle, Lightbulb, LightbulbFilament, Megaphone, MegaphoneSimple, Flag } from "@phosphor-icons/react";
import { useWakeLock } from "@/lib/wakeLock";
import { autoBlockName } from "@/lib/autoBlockName";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import TimerOverlay from "@/components/app/TimerOverlay";

// ─── audio helpers ──────────────────────────────────────────────
let audioCtx = null;
const getCtx = () => {
  if (typeof window === "undefined") return null;
  if (!audioCtx) {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    audioCtx = new Ctx();
  }
  if (audioCtx.state === "suspended") audioCtx.resume();
  return audioCtx;
};

const beep = (freq = 880, ms = 180, gain = 0.18) => {
  const ctx = getCtx();
  if (!ctx) return;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = "sine";
  osc.frequency.value = freq;
  g.gain.value = gain;
  osc.connect(g).connect(ctx.destination);
  osc.start();
  setTimeout(() => { try { osc.stop(); } catch { /* noop */ } }, ms);
};

// ─── voice helper (Web Speech API) ─────────────────────────────
// Speaks short cues — "5", "4", ..., "Go", "Rest", "Halfway", "Finish".
// Uses cancel() before each utterance so consecutive countdown numbers never
// queue up behind each other (which would lag behind the visual clock).
let _voiceCache = null;
const pickVoice = () => {
  if (typeof window === "undefined" || !window.speechSynthesis) return null;
  if (_voiceCache) return _voiceCache;
  const voices = window.speechSynthesis.getVoices() || [];
  // Prefer a high-quality English voice; fall back to any English voice.
  const preferred =
    voices.find((v) => /en-US/i.test(v.lang) && /female|samantha|google us english/i.test(v.name)) ||
    voices.find((v) => /en-US/i.test(v.lang)) ||
    voices.find((v) => /^en/i.test(v.lang)) ||
    voices[0];
  _voiceCache = preferred || null;
  return _voiceCache;
};

const speak = (text) => {
  if (typeof window === "undefined" || !window.speechSynthesis) return;
  try {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(String(text));
    u.rate = 1.1;
    u.pitch = 1.0;
    u.volume = 1.0;
    const v = pickVoice();
    if (v) u.voice = v;
    window.speechSynthesis.speak(u);
  } catch { /* ignore — voice is best-effort */ }
};

const fmt = (totalSec) => {
  const t = Math.max(0, Math.round(totalSec));
  const m = Math.floor(t / 60);
  const s = t % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

// ─── modes ──────────────────────────────────────────────────────
function modeOf(block) {
  const sc = block.scheme_config || {};
  switch (block.type) {
    case "metcon":
      return { kind: "stopwatch", capSec: sc.time_cap_seconds || null };
    case "amrap":
      return { kind: "countdown", capSec: sc.time_cap_seconds || 12 * 60 };
    case "emom":
      return {
        kind: "emom",
        rounds: sc.rounds || 10,
        intervalSec: sc.interval_seconds || 60,
        // EMOM rest = pause AFTER each work interval before the next round starts.
        // 0 (default) = classic EMOM with no built-in rest.
        restSec: sc.rest_seconds || 0,
      };
    case "tabata":
      return {
        kind: "tabata",
        rounds: sc.rounds || 8,
        workSec: sc.work_seconds || 20,
        restSec: sc.rest_seconds || 10,
      };
    case "intervals":
      // Work time set → work/rest interval clock; otherwise a stopwatch.
      if (sc.target_time_seconds) {
        return { kind: "tabata", rounds: sc.rounds || 5, workSec: sc.target_time_seconds, restSec: sc.rest_seconds || 0 };
      }
      return { kind: "stopwatch", capSec: null };
    case "circuit":
      // Circuit is treated like a strength superset — just track sets/reps/weights,
      // no timer needed. Returning null hides the "Start timer" entry point.
      return null;
    case "ladder":
      return { kind: "stopwatch", capSec: sc.time_cap_seconds || null };
    default:
      return null;
  }
}

// ─── core engine ────────────────────────────────────────────────
function useTimerEngine(mode, soundOn, voiceOn) {
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0); // ms since start of current segment / overall
  const [segIndex, setSegIndex] = useState(0); // round index for emom/tabata
  const [phase, setPhase] = useState("work"); // tabata + emom-with-rest: work | rest
  const [done, setDone] = useState(false);

  const startedAtRef = useRef(null);
  const accumRef = useRef(0);
  const rafRef = useRef(null);
  const lastWholeSecRef = useRef(-1);
  // Track whether "Halfway" has already been announced this session.
  const halfwayDoneRef = useRef(false);

  const reset = useCallback(() => {
    setRunning(false);
    setElapsed(0);
    setSegIndex(0);
    setPhase("work");
    setDone(false);
    startedAtRef.current = null;
    accumRef.current = 0;
    lastWholeSecRef.current = -1;
    halfwayDoneRef.current = false;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  }, []);

  const stop = useCallback(() => {
    // Hard-stop / "Finish" — freeze the clock at its current value, mark done.
    if (running) {
      accumRef.current += performance.now() - (startedAtRef.current ?? performance.now());
      startedAtRef.current = null;
    }
    setRunning(false);
    setDone(true);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  }, [running]);

  // tick loop
  useEffect(() => {
    if (!running || done) return;
    const tick = () => {
      const now = performance.now();
      const elapsedMs = accumRef.current + (now - (startedAtRef.current ?? now));
      setElapsed(elapsedMs);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [running, done]);

  // segment / cap detection (emom + tabata advance, countdown / cap end)
  // Voice cues piggyback on the same per-second tick: last 5 seconds of any
  // segment are spoken ("5, 4, 3, 2, 1"), then the transition label
  // ("Rest" / "Go" / "Finish") is spoken at 0.  "Halfway" fires once when
  // total elapsed crosses the midpoint (defined per mode below).
  useEffect(() => {
    if (!mode || done) return;
    const elapsedSec = Math.floor(elapsed / 1000);
    const isNewSec = elapsedSec !== lastWholeSecRef.current;

    // Helper: speak a countdown number only on the exact second tick (so we
    // don't double-speak when the effect re-runs for other reasons).
    const sayCountdown = (segRemaining) => {
      if (!voiceOn || !running || !isNewSec) return;
      if (segRemaining >= 1 && segRemaining <= 5) speak(String(segRemaining));
    };
    const sayHalfway = (total) => {
      if (!voiceOn || !running || halfwayDoneRef.current) return;
      if (total > 20 && elapsedSec >= Math.floor(total / 2) && isNewSec) {
        halfwayDoneRef.current = true;
        speak("Halfway");
      }
    };

    if (mode.kind === "stopwatch") {
      if (mode.capSec && elapsedSec >= mode.capSec && elapsedSec !== lastWholeSecRef.current) {
        if (elapsedSec === mode.capSec && soundOn) beep(440, 600, 0.22);
      }
      if (mode.capSec) {
        const remaining = mode.capSec - elapsedSec;
        sayCountdown(remaining);
        if (remaining === 0 && isNewSec && voiceOn && running) speak("Finish");
        sayHalfway(mode.capSec);
      }
    }

    if (mode.kind === "countdown") {
      const remaining = (mode.capSec || 0) - elapsedSec;
      sayCountdown(remaining);
      if (remaining <= 0) {
        if (running) {
          if (soundOn) beep(440, 700, 0.22);
          if (voiceOn) speak("Finish");
          setDone(true);
          setRunning(false);
        }
      } else if (remaining <= 3 && remaining !== lastWholeSecRef.current) {
        if (soundOn) beep(880, 120, 0.18);
      }
      sayHalfway(mode.capSec || 0);
    }

    if (mode.kind === "emom") {
      const cycle = mode.intervalSec + (mode.restSec || 0);
      const totalSec = mode.rounds * cycle;
      const round = Math.floor(elapsedSec / cycle);
      const inCycle = elapsedSec - round * cycle;
      const newPhase = inCycle < mode.intervalSec ? "work" : "rest";
      const segLen = newPhase === "work" ? mode.intervalSec : (mode.restSec || 0);
      const segElapsed = newPhase === "work" ? inCycle : (inCycle - mode.intervalSec);
      const segRemaining = segLen - segElapsed;
      sayCountdown(segRemaining);
      sayHalfway(totalSec);
      if (round >= mode.rounds) {
        if (running) {
          if (soundOn) beep(440, 700, 0.22);
          if (voiceOn) speak("Finish");
          setDone(true);
          setRunning(false);
        }
      } else if (round !== segIndex || newPhase !== phase) {
        setSegIndex(round);
        setPhase(newPhase);
        if (soundOn) beep(newPhase === "work" ? 1100 : 600, 220, 0.22);
        // Speak transition: entering rest → "Rest"; entering work mid-workout → "Go".
        // First time we transition into work on round 0 we already spoke "Go" via Start.
        if (voiceOn && (round > 0 || newPhase === "rest")) {
          speak(newPhase === "rest" ? "Rest" : "Go");
        }
      }
    }

    if (mode.kind === "tabata") {
      const cycle = mode.workSec + mode.restSec;
      const totalSec = mode.rounds * cycle;
      const round = Math.floor(elapsedSec / cycle);
      const inCycle = elapsedSec - round * cycle;
      const newPhase = inCycle < mode.workSec ? "work" : "rest";
      const segLen = newPhase === "work" ? mode.workSec : mode.restSec;
      const segElapsed = newPhase === "work" ? inCycle : (inCycle - mode.workSec);
      const segRemaining = segLen - segElapsed;
      sayCountdown(segRemaining);
      sayHalfway(totalSec);
      if (round >= mode.rounds) {
        if (running) {
          if (soundOn) beep(440, 700, 0.22);
          if (voiceOn) speak("Finish");
          setDone(true);
          setRunning(false);
        }
      } else if (round !== segIndex || newPhase !== phase) {
        setSegIndex(round);
        setPhase(newPhase);
        if (soundOn) beep(newPhase === "work" ? 1100 : 600, 220, 0.22);
        if (voiceOn && (round > 0 || newPhase === "rest")) {
          speak(newPhase === "rest" ? "Rest" : "Go");
        }
      }
    }

    lastWholeSecRef.current = elapsedSec;
  }, [elapsed, mode, running, done, segIndex, phase, soundOn, voiceOn]);

  const start = () => {
    if (done) return;
    startedAtRef.current = performance.now();
    setRunning(true);
    getCtx();
    if (soundOn) beep(880, 120, 0.18);
  };
  const pause = () => {
    if (!running) return;
    accumRef.current += performance.now() - (startedAtRef.current ?? performance.now());
    startedAtRef.current = null;
    setRunning(false);
  };

  return { running, elapsed, segIndex, phase, done, start, pause, reset, stop };
}

// ─── UI ─────────────────────────────────────────────────────────
const PREP_OPTIONS = [30, 20, 10, 5, 0];

function TimerBody({ block, onFinish, minimized = false }) {
  const mode = modeOf(block);
  const [soundOn, setSoundOn] = useState(true);
  // Voice cues (Web Speech API) — persist preference per-browser so the
  // athlete's setting carries across sessions.
  const [voiceOn, setVoiceOn] = useState(() => {
    try { return localStorage.getItem("hs-timer-voice") !== "off"; }
    catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem("hs-timer-voice", voiceOn ? "on" : "off"); }
    catch { /* ignore */ }
  }, [voiceOn]);
  // Warm up the voice list on mount — some browsers populate voices async.
  useEffect(() => {
    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.getVoices();
      window.speechSynthesis.onvoiceschanged = () => { _voiceCache = null; };
    }
  }, []);
  const [keepAwake, setKeepAwake] = useState(true);
  // Pre-countdown ("on the line"). Default 10s — a comfortable middle.
  const [prepSeconds, setPrepSeconds] = useState(10);
  const [prepRemaining, setPrepRemaining] = useState(0); // 0 = not in prep
  const prepRafRef = useRef(null);
  const prepStartedAtRef = useRef(0);

  const { running, elapsed, segIndex, phase, done, start, pause, reset, stop } = useTimerEngine(mode, soundOn, voiceOn);
  const wake = useWakeLock(keepAwake);

  // Round / rep counter (manual, athlete taps to bump).
  // Available on stopwatch (MetCon, RFT, Circuit, Ladder) and AMRAP countdown.
  const [rounds, setRounds] = useState(0);
  const [reps, setReps] = useState(0);
  const isIntervals = block.type === "intervals";
  const showCounter = mode && !isIntervals && (mode.kind === "stopwatch" || mode.kind === "countdown");

  // Intervals: live per-interval results the athlete fills in while the clock runs.
  const ivlGoal = goalOf(block.scheme_config);
  const freshIvlRows = () => Array.from({ length: Math.max(1, Number(block.scheme_config?.rounds) || 1) }, emptyIntervalRow);
  const [ivlRows, setIvlRows] = useState(freshIvlRows);
  const [ivlUnit, setIvlUnit] = useState(block.scheme_config?.target_distance_unit || "m");
  const [lapIdx, setLapIdx] = useState(0);
  const lapAtRef = useRef(0);

  // Reset counter whenever the timer is reset.
  useEffect(() => {
    if (elapsed === 0 && !running && !done) {
      setRounds(0);
      setReps(0);
      setLapIdx(0);
      lapAtRef.current = 0;
    }
  }, [elapsed, running, done]);

  // Fixed work-time intervals: an interval counts as finished once its work portion ends.
  const workDone = isIntervals && mode?.kind === "tabata"
    ? Math.min(mode.rounds, Math.floor((Math.floor(elapsed / 1000) + mode.restSec) / (mode.workSec + mode.restSec)))
    : 0;
  const withWorkTimes = useCallback((rows) => {
    if (workDone <= 0) return rows;
    const w = splitSeconds(mode.workSec);
    let changed = false;
    const next = rows.map((r, i) => {
      if (i < workDone && r.min === "" && r.sec === "") { changed = true; return { ...r, min: w.min, sec: w.sec }; }
      return r;
    });
    return changed ? next : rows;
  }, [workDone, mode?.workSec]);
  useEffect(() => { setIvlRows(withWorkTimes); }, [withWorkTimes]);

  const lap = () => {
    const now = Math.round(elapsed / 1000);
    const split = splitSeconds(now - lapAtRef.current);
    lapAtRef.current = now;
    setIvlRows((rows) => {
      const next = rows.length > lapIdx ? [...rows] : [...rows, emptyIntervalRow()];
      next[lapIdx] = { ...next[lapIdx], min: split.min, sec: split.sec };
      return next;
    });
    setLapIdx((i) => i + 1);
  };

  // ── Pre-countdown ("on the line, ready, go!") ───────────────────
  // When user clicks Start with prepSeconds > 0, run a countdown overlay
  // first. Beeps at 3-2-1, longer beep at 0, then hand off to engine.start().
  const beginPrep = useCallback(() => {
    if (prepSeconds <= 0) {
      start();
      return;
    }
    // Wake the audio ctx now so the first beep isn't silently swallowed.
    getCtx();
    if (soundOn) beep(880, 120, 0.18);
    prepStartedAtRef.current = performance.now();
    setPrepRemaining(prepSeconds);
  }, [prepSeconds, soundOn, start]);

  const cancelPrep = useCallback(() => {
    if (prepRafRef.current) cancelAnimationFrame(prepRafRef.current);
    setPrepRemaining(0);
  }, []);

  const skipPrep = useCallback(() => {
    cancelPrep();
    start();
  }, [cancelPrep, start]);

  useEffect(() => {
    if (prepRemaining <= 0) return undefined;
    const tick = () => {
      const elapsedPrepMs = performance.now() - prepStartedAtRef.current;
      const remaining = Math.max(0, prepSeconds - Math.floor(elapsedPrepMs / 1000));
      setPrepRemaining((cur) => {
        if (remaining !== cur) {
          // Beep on each new whole second for the last 3, longer beep at 0.
          if (soundOn) {
            if (remaining === 0) beep(1320, 500, 0.25);
            else if (remaining <= 3) beep(880, 140, 0.2);
          }
          // Voice: "5 … 4 … 3 … 2 … 1 … Go" for the final stretch.
          if (voiceOn) {
            if (remaining === 0) speak("Go");
            else if (remaining <= 5) speak(String(remaining));
          }
        }
        return remaining;
      });
      if (remaining <= 0) {
        cancelAnimationFrame(prepRafRef.current);
        prepRafRef.current = null;
        start();
        return;
      }
      prepRafRef.current = requestAnimationFrame(tick);
    };
    prepRafRef.current = requestAnimationFrame(tick);
    return () => { if (prepRafRef.current) cancelAnimationFrame(prepRafRef.current); };
  }, [prepRemaining, prepSeconds, soundOn, voiceOn, start]);

  // Reset wipes the prep countdown too.
  const fullReset = useCallback(() => {
    cancelPrep();
    reset();
  }, [cancelPrep, reset]);

  // Fire onFinish whenever the engine transitions to done (manual Finish OR
  // auto-completion at cap / last round). We pass the counter + elapsed so the
  // parent can auto-open the completion dialog with prefilled values.
  const firedRef = useRef(false);
  useEffect(() => {
    if (done && !firedRef.current) {
      firedRef.current = true;
      onFinish?.({
        block,
        elapsedSec: Math.round(elapsed / 1000),
        rounds,
        reps,
        intervalRows: isIntervals ? withWorkTimes(ivlRows) : null,
        intervalUnit: ivlUnit,
      });
    }
    if (!done) firedRef.current = false;
  }, [done, elapsed, rounds, reps, block, onFinish, isIntervals, ivlRows, ivlUnit, withWorkTimes]);

  if (!mode) return null;
  const elapsedSec = elapsed / 1000;
  const movements = block.movements || [];

  let bigText = fmt(elapsedSec);
  let subText = "";
  let accent = "text-moss";

  if (mode.kind === "stopwatch") {
    subText = mode.capSec ? `Cap ${fmt(mode.capSec)}` : "For time";
    if (mode.capSec && elapsedSec >= mode.capSec) accent = "text-error";
  } else if (mode.kind === "countdown") {
    const remaining = Math.max(0, (mode.capSec || 0) - elapsedSec);
    bigText = fmt(remaining);
    subText = `AMRAP · ${fmt(mode.capSec)} cap`;
    if (remaining <= 10) accent = "text-error";
  } else if (mode.kind === "emom") {
    const cycle = mode.intervalSec + (mode.restSec || 0);
    const inCycle = elapsedSec - segIndex * cycle;
    const phaseLen = phase === "work" ? mode.intervalSec : cycle;
    const remaining = Math.max(0, phaseLen - inCycle);
    bigText = fmt(remaining);
    const mvName = movements[segIndex % Math.max(1, movements.length)]?.name;
    const phaseLabel = mode.restSec > 0 ? ` · ${phase === "work" ? "WORK" : "REST"}` : "";
    subText = `Round ${Math.min(segIndex + 1, mode.rounds)} / ${mode.rounds}${phaseLabel}${mvName && phase === "work" ? ` · ${mvName}` : ""}`;
    if (mode.restSec > 0) {
      accent = phase === "work" ? "text-moss" : "text-terracotta";
    } else if (remaining <= 5) {
      accent = "text-warning";
    }
  } else if (mode.kind === "tabata") {
    const cycle = mode.workSec + mode.restSec;
    const inCycle = elapsedSec - segIndex * cycle;
    const phaseLen = phase === "work" ? mode.workSec : mode.workSec + mode.restSec;
    const remaining = Math.max(0, phaseLen - inCycle);
    bigText = fmt(remaining);
    subText = `Round ${Math.min(segIndex + 1, mode.rounds)} / ${mode.rounds} · ${phase === "work" ? "WORK" : "REST"}`;
    accent = phase === "work" ? "text-terracotta" : "text-moss";
  }

  // EMOM-with-rest header: render "EMOM 45/15 - 12" style sub-label.
  const headerSub = mode.kind === "emom" && mode.restSec > 0
    ? `EMOM ${mode.intervalSec}/${mode.restSec} · ${mode.rounds} rds`
    : null;

  // ── Compact (minimized / corner-pinned) rendering ──────────────────
  // Same engine, lean UI: status pill, big clock, single primary action,
  // and a Finish escape hatch. The full UI returns when the user restores
  // the overlay — engine state is preserved because TimerBody stays mounted.
  if (minimized) {
    return (
      <div className="space-y-2" data-testid={`block-timer-mini-${block.id}`}>
        {prepRemaining > 0 ? (
          <div className="text-center">
            <div className="hs-label text-[9px] text-bone/60">Get ready</div>
            <div className="font-data font-semibold leading-none text-bone" style={{ fontSize: "2.75rem" }}
                 data-testid="prep-countdown-value-mini">
              {prepRemaining}
            </div>
          </div>
        ) : (
          <div className="text-center">
            <div className="hs-label text-[9px] text-bone/60 truncate">
              {headerSub || (block.title || block.type?.toUpperCase())}
              {done ? " · DONE" : running ? " · LIVE" : " · READY"}
            </div>
            <div className={`font-data font-semibold leading-none ${accent}`} style={{ fontSize: "2.75rem" }} data-testid="timer-display-mini">
              {bigText}
            </div>
            {subText && <div className="mt-1 text-[10px] text-bone/70 truncate">{subText}</div>}
          </div>
        )}

        {showCounter && (
          <div className="grid grid-cols-2 gap-1.5 text-[10px] font-data" data-testid="round-rep-counter-mini">
            <button
              type="button"
              onClick={() => setRounds((r) => r + 1)}
              className="rounded-lg bg-bone/10 hover:bg-bone/20 px-2 py-1 text-bone"
              data-testid="counter-rounds-plus-mini"
              aria-label="Tap to add round"
            >
              <span className="opacity-60">Rds</span> <span className="font-semibold text-sm">{rounds}</span> <Plus size={10} weight="bold" className="inline" />
            </button>
            <button
              type="button"
              onClick={() => setReps((r) => r + 1)}
              className="rounded-lg bg-bone/10 hover:bg-bone/20 px-2 py-1 text-bone"
              data-testid="counter-reps-plus-mini"
              aria-label="Tap to add rep"
            >
              <span className="opacity-60">Rep</span> <span className="font-semibold text-sm">{reps}</span> <Plus size={10} weight="bold" className="inline" />
            </button>
          </div>
        )}

        <div className="flex items-center justify-center gap-1.5">
          {prepRemaining > 0 ? (
            <Button onClick={skipPrep} size="sm" className="rounded-lg h-7 px-2.5 text-[11px] bg-terracotta hover:bg-terracotta-hover text-white hover:text-white" data-testid="timer-skip-prep-mini">
              Skip
            </Button>
          ) : !running ? (
            <Button onClick={beginPrep} disabled={done} size="sm" className="rounded-lg h-7 px-2.5 text-[11px] bg-terracotta hover:bg-terracotta-hover text-white hover:text-white" data-testid="timer-start-mini">
              <Play size={11} className="mr-1" weight="fill" /> {elapsed > 0 ? "Go" : "Start"}
            </Button>
          ) : (
            <Button onClick={pause} size="sm" className="rounded-lg h-7 px-2.5 text-[11px] bg-warning hover:bg-warning text-white hover:text-white" data-testid="timer-pause-mini">
              <Pause size={11} className="mr-1" weight="fill" /> Pause
            </Button>
          )}
          <Button
            onClick={stop}
            disabled={done || elapsed === 0 || prepRemaining > 0}
            size="sm"
            className="rounded-lg h-7 px-2.5 text-[11px] bg-bone text-ink hover:bg-bone hover:text-ink border-0 disabled:opacity-50"
            data-testid="timer-stop-mini"
          >
            <StopCircle size={11} className="mr-1" weight="fill" /> Finish
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid={`block-timer-${block.id}`}>
      {prepRemaining > 0 && (
        <div className="rounded-2xl bg-ink text-bone p-6 text-center" data-testid="prep-countdown">
          <div className="hs-label text-[10px] text-bone/70">Get ready</div>
          <div className="font-data font-semibold leading-none text-bone" style={{ fontSize: "5rem" }}
               data-testid="prep-countdown-value">
            {prepRemaining}
          </div>
          <div className="mt-2 text-xs text-bone/80 uppercase tracking-[0.2em]">
            Starting in {prepRemaining}s · <button onClick={skipPrep} className="underline" data-testid="prep-skip">skip</button>
          </div>
        </div>
      )}

      <div className={`text-center ${isIntervals ? "sticky -top-5 z-10 bg-surface pt-3 pb-2 -mx-5 px-5 border-b border-line" : ""}`}>
        <div className="hs-label text-[10px] mb-1">
          {headerSub || (block.title || block.type?.toUpperCase())}
          {done ? " · DONE" : running ? " · LIVE" : prepRemaining > 0 ? " · STANDBY" : " · READY"}
        </div>
        <div className={`font-data font-semibold leading-none ${accent}`} style={{ fontSize: "5rem" }} data-testid="timer-display">
          {bigText}
        </div>
        <div className="mt-2 text-sm text-ink-2">{subText}</div>
      </div>

      {mode.kind === "emom" && movements.length > 1 && (
        <div className="grid grid-cols-2 gap-2 text-xs font-data">
          {movements.map((m, i) => (
            <div key={m.id || i}
              className={`px-2 py-1.5 rounded-lg border ${i === (segIndex % movements.length) && !done && phase === "work" ? "bg-moss text-white border-moss" : "border-line text-ink-2"}`}>
              <span className="opacity-70 mr-1">#{i + 1}</span>{m.name}
            </div>
          ))}
        </div>
      )}

      {showCounter && (
        <div className="grid grid-cols-2 gap-3" data-testid="round-rep-counter">
          <div className="rounded-2xl border border-line bg-muted2/60 p-3">
            <div className="hs-label text-[10px] text-center">Rounds</div>
            <div className="mt-1 flex items-center justify-between gap-2">
              <Button
                size="icon"
                variant="ghost"
                onClick={() => setRounds((r) => Math.max(0, r - 1))}
                className="rounded-full h-9 w-9"
                aria-label="Decrement rounds"
                data-testid="counter-rounds-minus"
              >
                <Minus size={16} />
              </Button>
              <button
                type="button"
                onClick={() => setRounds((r) => r + 1)}
                className="font-data font-semibold text-3xl text-ink min-w-[2.5ch] text-center hover:text-moss"
                aria-label="Tap to add round"
                data-testid="counter-rounds-value"
              >
                {rounds}
              </button>
              <Button
                size="icon"
                onClick={() => setRounds((r) => r + 1)}
                className="rounded-full h-9 w-9 bg-moss hover:bg-moss-hover text-white hover:text-white"
                aria-label="Increment rounds"
                data-testid="counter-rounds-plus"
              >
                <Plus size={16} weight="bold" />
              </Button>
            </div>
          </div>
          <div className="rounded-2xl border border-line bg-muted2/60 p-3">
            <div className="hs-label text-[10px] text-center">Reps</div>
            <div className="mt-1 flex items-center justify-between gap-2">
              <Button
                size="icon"
                variant="ghost"
                onClick={() => setReps((r) => Math.max(0, r - 1))}
                className="rounded-full h-9 w-9"
                aria-label="Decrement reps"
                data-testid="counter-reps-minus"
              >
                <Minus size={16} />
              </Button>
              <button
                type="button"
                onClick={() => setReps((r) => r + 1)}
                className="font-data font-semibold text-3xl text-ink min-w-[2.5ch] text-center hover:text-terracotta"
                aria-label="Tap to add rep"
                data-testid="counter-reps-value"
              >
                {reps}
              </button>
              <Button
                size="icon"
                onClick={() => setReps((r) => r + 1)}
                className="rounded-full h-9 w-9 bg-terracotta hover:bg-terracotta-hover text-white hover:text-white"
                aria-label="Increment reps"
                data-testid="counter-reps-plus"
              >
                <Plus size={16} weight="bold" />
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-center gap-2 flex-wrap">
        {!running && prepRemaining === 0 && (
          <div className="w-full flex items-center justify-center gap-2 text-xs text-ink-2 mb-1" data-testid="prep-picker">
            <span className="hs-label text-[10px]">Countdown</span>
            {PREP_OPTIONS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setPrepSeconds(s)}
                className={`px-2.5 py-1 rounded-full font-data text-[11px] font-semibold transition-colors ${
                  prepSeconds === s ? "bg-moss text-white" : "bg-muted2 text-ink-2 hover:bg-sage hover:text-moss"
                }`}
                aria-label={`${s} second countdown`}
                data-testid={`prep-${s}`}
              >
                {s === 0 ? "Off" : `${s}s`}
              </button>
            ))}
          </div>
        )}
        {!running && prepRemaining === 0 ? (
          <Button onClick={beginPrep} disabled={done} className="rounded-xl bg-terracotta hover:bg-terracotta-hover text-white hover:text-white" data-testid="timer-start">
            <Play size={16} className="mr-2" weight="fill" /> {elapsed > 0 ? "Resume" : "Start"}
          </Button>
        ) : prepRemaining > 0 ? (
          <Button onClick={cancelPrep} variant="secondary" className="rounded-xl" data-testid="timer-cancel-prep">
            Cancel
          </Button>
        ) : (
          <Button onClick={pause} className="rounded-xl bg-warning hover:bg-warning text-white hover:text-white" data-testid="timer-pause">
            <Pause size={16} className="mr-2" weight="fill" /> Pause
          </Button>
        )}
        <Button
          onClick={stop}
          disabled={done || elapsed === 0 || prepRemaining > 0}
          variant="secondary"
          className="rounded-xl bg-ink/90 hover:bg-ink text-white hover:text-white border-0 disabled:opacity-50"
          data-testid="timer-stop"
        >
          <StopCircle size={16} className="mr-2" weight="fill" /> Finish
        </Button>
        <Button variant="secondary" onClick={fullReset} className="rounded-xl" data-testid="timer-reset">
          <ArrowCounterClockwise size={16} className="mr-2" /> Reset
        </Button>
        <Button
          variant="ghost"
          onClick={() => setSoundOn((s) => !s)}
          className="rounded-xl"
          aria-label={soundOn ? "Mute timer beeps" : "Unmute timer beeps"}
          data-testid="timer-sound-toggle"
        >
          {soundOn ? <SpeakerHigh size={16} /> : <SpeakerSlash size={16} />}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            const next = !voiceOn;
            setVoiceOn(next);
            // Demo cue so the athlete instantly knows voice came on.
            if (next) setTimeout(() => speak("Voice on"), 0);
            else if (typeof window !== "undefined" && window.speechSynthesis) {
              window.speechSynthesis.cancel();
            }
          }}
          className={`rounded-xl ${voiceOn ? "text-moss" : "text-ink-2"}`}
          aria-label={voiceOn ? "Mute voice cues" : "Enable voice cues"}
          title={voiceOn ? "Voice cues on (5..1, Go, Rest, Halfway, Finish)" : "Voice cues off"}
          data-testid="timer-voice-toggle"
        >
          {voiceOn ? <Megaphone size={16} weight="fill" /> : <MegaphoneSimple size={16} />}
        </Button>
        {wake.supported && (
          <Button
            variant="ghost"
            onClick={() => setKeepAwake((k) => !k)}
            className={`rounded-xl ${wake.locked ? "text-moss" : "text-ink-2"}`}
            aria-label={keepAwake ? "Allow screen to sleep" : "Keep screen awake"}
            title={wake.locked ? "Screen will stay awake" : keepAwake ? "Wake-lock pending…" : "Screen may sleep"}
            data-testid="timer-wake-toggle"
          >
            {keepAwake ? <LightbulbFilament size={16} weight="fill" /> : <Lightbulb size={16} />}
          </Button>
        )}
      </div>
      {wake.supported && wake.locked && keepAwake && (
        <div className="text-center text-[10px] uppercase tracking-wider text-ink-2/70" data-testid="wake-lock-active">
          Screen will stay on
        </div>
      )}
      {isIntervals && (
        <div className="rounded-2xl border border-line p-3 space-y-2" data-testid="timer-interval-log">
          {mode.kind === "stopwatch" && (
            <Button onClick={lap} disabled={!running} className="w-full rounded-xl bg-moss hover:bg-moss-hover text-white hover:text-white"
              data-testid="timer-interval-lap">
              <Flag size={16} className="mr-2" weight="fill" /> Lap · finish interval #{lapIdx + 1}
            </Button>
          )}
          <IntervalResultsInput goal={ivlGoal} rows={ivlRows} setRows={setIvlRows}
            distanceUnit={ivlUnit} setDistanceUnit={setIvlUnit}
            activeIndex={done ? null : mode.kind === "tabata" ? segIndex : lapIdx} />
          <p className="text-[10px] text-ink-3">Type each interval&rsquo;s time, distance and calories as you go — they&rsquo;re saved when you tap Finish.</p>
        </div>
      )}
    </div>
  );
}

import EditLogDialog from "@/components/app/EditLogDialog";
import BlockCompletionDialog from "@/components/blocks/BlockCompletionDialog";
import IntervalResultsInput, { emptyIntervalRow } from "@/components/blocks/IntervalResultsInput";
import { goalOf, splitSeconds, buildIntervalPayload } from "@/lib/intervals";

export default function BlockTimer({ block, trigger, program, dayIdx, athleteId, onDone }) {
  const [open, setOpen] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [editorKey, setEditorKey] = useState(0);
  const [intervalLogId, setIntervalLogId] = useState(null);
  const [intervalPrefill, setIntervalPrefill] = useState(null);
  const finishingRef = useRef(false);
  const mode = modeOf(block);
  if (!mode) return null;

  const handleFinish = async ({ elapsedSec, rounds, reps, intervalRows, intervalUnit }) => {
    // Guard against a missing program context — surface visibly instead of
    // silently dropping the result so the user is never left wondering why
    // nothing was saved.
    if (!program?.id) {
      toast.error("Couldn't save — this timer isn't part of a programmed workout");
      return;
    }
    if (finishingRef.current) return;
    finishingRef.current = true;
    setTimeout(() => { finishingRef.current = false; }, 1500);

    const name = autoBlockName(block);
    const blockRepScheme = block.scheme_config?.rep_scheme && block.scheme_config.rep_scheme.length
      ? block.scheme_config.rep_scheme : null;
    const ivl = intervalRows ? buildIntervalPayload(goalOf(block.scheme_config), intervalRows, intervalUnit) : null;
    const ivlFields = ivl && ivl.interval_results.length
      ? { ...ivl, total_time_seconds: ivl.total_time_seconds ?? (elapsedSec > 0 ? elapsedSec : null) }
      : {};
    try {
      const { data: savedLog } = await api.post("/workout-logs", {
        program_id: program.id,
        day_of_week: dayIdx ?? 0,
        set_number: 1,
        block_id: block.id,
        block_type: block.type,
        block_title: name,
        total_time_seconds: elapsedSec > 0 ? elapsedSec : null,
        rounds_completed: rounds > 0 ? rounds : null,
        extra_reps: reps > 0 ? reps : null,
        block_rep_scheme: blockRepScheme,
        ...ivlFields,
      });
      // Also save a child log per programmed movement so reps/weight/distance/
      // height/calories/RPE/per-movement-time are all preserved in history.
      // Each movement uses its programmed defaults; the athlete can edit
      // any of these afterwards from the History page.
      const childLogs = await Promise.allSettled(
        (block.movements || []).map((mv, idx) => api.post("/workout-logs", {
          program_id: program.id,
          day_of_week: dayIdx ?? 0,
          set_number: idx + 1,
          block_id: block.id,
          block_type: block.type,
          block_title: name,
          movement_id: mv.id,
          movement_name: mv.name,
          reps: mv.reps ?? null,
          weight: mv.weight ?? null,
          weight_unit: mv.weight_unit ?? null,
          time_seconds: mv.time_seconds ?? null,
          distance_meters: mv.distance_meters ?? null,
          distance_unit: mv.distance_unit ?? null,
          height_inches: mv.height_inches ?? null,
          height_unit: mv.height_unit ?? null,
          calories: mv.calories ?? null,
          rpe: mv.rpe ?? null,
          notes: null,
          block_rep_scheme: (mv.reps_scheme && mv.reps_scheme.length)
            ? mv.reps_scheme
            : blockRepScheme,
        })),
      );
      const failed = childLogs.filter((r) => r.status === "rejected").length;
      if (failed > 0) {
        toast.warning(`Section saved, but ${failed} movement detail${failed > 1 ? "s" : ""} failed to log — you can re-enter from History.`);
      }
      setTimeout(() => setOpen(false), 120);
      if (block.type === "intervals") {
        setIntervalPrefill({ rows: intervalRows, unit: intervalUnit });
        setIntervalLogId(savedLog.id);
      }
      const minutes = Math.floor(elapsedSec / 60);
      const seconds = elapsedSec % 60;
      toast.success(`Saved · ${name}`, {
        description: `${minutes ? `${minutes}m ` : ""}${seconds}s${rounds > 0 ? ` · ${rounds} rounds` : ""}${reps > 0 ? ` + ${reps} reps` : ""}`,
        action: {
          label: "Edit",
          onClick: () => {
            setEditingLog(savedLog);
            setEditorKey((k) => k + 1);
          },
        },
        duration: 6000,
      });
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <>
      {trigger ? (
        React.cloneElement(trigger, { onClick: () => setOpen(true) })
      ) : (
        <Button
          variant="secondary"
          className="w-full rounded-xl"
          onClick={() => setOpen(true)}
          data-testid={`open-timer-${block.id}`}
        >
          <TimerIcon size={16} className="mr-2" weight="bold" /> Start timer
        </Button>
      )}

      <TimerOverlay
        open={open}
        onClose={() => setOpen(false)}
        title={block.title || block.type?.toUpperCase()}
      >
        {({ minimized }) => (
          <TimerBody block={block} onFinish={handleFinish} minimized={minimized} />
        )}
      </TimerOverlay>

      {intervalLogId && (
        <BlockCompletionDialog
          block={block}
          program={program}
          dayIdx={dayIdx}
          athleteId={athleteId}
          open
          onOpenChange={(o) => { if (!o) setIntervalLogId(null); }}
          hideTrigger
          existingLogId={intervalLogId}
          intervalRows={intervalPrefill?.rows}
          intervalUnit={intervalPrefill?.unit}
          onDone={() => { setIntervalLogId(null); onDone?.(); }}
        />
      )}

      {editingLog && (
        <EditLogAutoOpen
          key={editorKey}
          log={editingLog}
          onClose={() => setEditingLog(null)}
          onSaved={() => { setEditingLog(null); onDone?.(); }}
        />
      )}
    </>
  );
}

/** Wrapper that auto-opens EditLogDialog by simulating a click on its trigger. */
function EditLogAutoOpen({ log, onClose, onSaved }) {
  const triggerRef = useRef(null);
  useEffect(() => {
    // Open on next tick so React finishes mounting the trigger.
    const id = setTimeout(() => triggerRef.current?.click(), 0);
    return () => clearTimeout(id);
  }, []);
  return (
    <EditLogDialog
      log={log}
      onSaved={(updated) => { onSaved?.(updated); onClose?.(); }}
      trigger={<button ref={triggerRef} type="button" className="hidden" aria-hidden="true" />}
    />
  );
}
